import ApiHttp from "byzantine/src/ApiHttp";

const POLICY_URL = "thread_files/policies";

export const openFileDialog = () =>
  new Promise((resolve) => {
    const fileElement = document.createElement("input");
    fileElement.type = "file";
    fileElement.addEventListener("change", (e) => {
      const file = e.target.files[0] || null;
      resolve(file);
    });
    const cancelHandler = () => {
      if (fileElement.value.length === 0) {
        setTimeout(() => {
          if (fileElement.files.length === 0) {
            resolve(null);
          }
        }, 500);
      }
      document.body.removeEventListener("focus", cancelHandler, true);
    };
    document.body.addEventListener("focus", cancelHandler, true);
    fileElement.click();
  });

const createUploadPolicy = (file) => {
  const payload = { type: file.type, name: file.name };
  return ApiHttp.fetch(POLICY_URL, { method: "POST" }, payload);
};

export const getUrlFromS3XMLResponse = (xml) => {
  const tree = new DOMParser().parseFromString(xml, "text/xml");
  const tag = tree.getElementsByTagName("Location")[0];
  const url = decodeURIComponent(tag.childNodes[0].nodeValue);
  return url;
};

export const uploadFile = async (file) => {
  const { form_action: url, signature, ...data } = await createUploadPolicy(file);

  const formData = new FormData();
  Object.keys(data).forEach((key) => formData.append(key, data[key]));
  formData.append("file", file);

  const xmlData = await ApiHttp.fetch(
    url,
    { method: "POST", endpointIsUrl: true },
    formData
  );
  const fileUrl = getUrlFromS3XMLResponse(xmlData);
  return { url: fileUrl, signature };
};

/**
 * Sanitizes a filename by removing hidden unicode characters, normalizing diacritics,
 * removing special characters, and replacing spaces with hyphens.
 *
 * @param filename - The filename to sanitize
 * @returns The sanitized filename safe for use in downloads
 */
export const sanitizeFilename = (filename) =>
  // Remove hidden unicode characters and normalize the string
  filename
    .normalize("NFD") // Decompose unicode characters
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritics/combining characters
    .replace(/[\u200b-\u200f\u2028-\u202f\u205f-\u206f]/g, "") // Remove zero-width and other hidden characters
    .replace(/[^\w\s.-]/g, "") // Keep only word characters, spaces, dots, and hyphens
    .trim() // Trim whitespace before replacing spaces with hyphens
    .replace(/\s+/g, "-"); // Replace spaces with hyphens

/**
 * Downloads an image from base64 data by creating a temporary anchor element.
 * The filename is automatically sanitized to ensure compatibility across different file systems.
 *
 * @param {string} base64Data - The base64 encoded image data (without the data URL prefix)
 * @param {string} filename - The desired filename for the download (will be sanitized)
 */
export const downloadImage = (base64Data, filename) => {
  const sanitizedFilename = sanitizeFilename(filename);
  const link = document.createElement("a");
  link.href = `data:image/jpeg;base64,${base64Data}`;
  link.download = sanitizedFilename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default {
  openFileDialog,
  uploadFile,
};
